import {TLevelObject} from "../../config/types";
import type GameObject from "../game/GameObject.js";
import {Vec2} from "./Vec2.js";

/**
 * Axis-Aligned Bounding Box (AABB) class for collision detection and spatial queries.
 * Represents a rectangle with sides parallel to the coordinate axes.
 */
export class AABB {
  /**
   * Creates a new AABB with the specified bounds.
   * @param lowerBound The minimum coordinates (bottom-left corner)
   * @param upperBound The maximum coordinates (top-right corner)
   */
  constructor(
    public lowerBound: Vec2,
    public upperBound: Vec2,
  ) {}

  /**
   * Creates an empty AABB with infinite bounds.
   * Useful as a starting point for computing bounds of multiple objects.
   * @returns An AABB with lowerBound set to (Infinity, Infinity) and upperBound set to (-Infinity, -Infinity)
   */
  static empty(): AABB {
    return new AABB(
      new Vec2(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
      new Vec2(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY),
    );
  }

  /**
   * Computes the AABB of an object.
   * This method handles rotation by calculating the corners of the rotated rectangle.
   * For non-rotated objects, it uses a simpler calculation.
   * @returns An AABB instance representing the bounds
   */
  static from(position: Vec2, rotation: number, scale: Vec2): AABB {
    const halfScale = scale.clone().div(2);

    // If no rotation, we can simplify the calculation
    if (rotation === 0) {
      return new AABB(position.clone(), position.clone())
        .enlarge2D(halfScale)
        .finalize();
    }

    const corners = Vec2.corners();
    const mapped = corners.map((c) =>
      c.mulComponents(halfScale).rotate(rotation).add(position),
    );
    const reduced = mapped.reduce(
      (prev: AABB, curr) => prev.mergeWithPoint(curr),
      AABB.empty(),
    );
    return reduced.finalize();
    // return corners
    //   .map((c) => c.mul(halfScale).rotate(rotation).add(position))
    //   .reduce((prev: AABB, curr) => prev.mergeWithPoint(curr), AABB.empty())
    //   .finalize();
  }

  /**
   * Computes the AABB of a GameObject.
   * This method handles rotation by calculating the corners of the rotated rectangle.
   * For non-rotated objects, it uses a simpler calculation.
   * @returns An AABB instance representing the bounds
   */
  static fromGameObject(obj: GameObject): AABB {
    return AABB.from(obj.position, obj.rotation, obj.scale);
  }

  /**
   * Computes the AABB of a TLevelObject.
   * This method handles rotation by calculating the corners of the rotated rectangle.
   * For non-rotated objects, it uses a simpler calculation.
   * @returns An AABB instance representing the bounds
   */
  static fromTLevelObject(obj: TLevelObject): AABB {
    return AABB.from(
      new Vec2(...obj.position),
      obj.rotation,
      new Vec2(...obj.scale),
    );
  }

  /**
   * Creates an AABB that encompasses all the given objects.
   * If no objects exist, returns a degenerate AABB at the origin.
   */
  static fromTLevelObjects(objects: TLevelObject[]): AABB {
    return objects
      .map(AABB.fromTLevelObject)
      .reduce((prev, curr) => prev.merge(curr), AABB.empty())
      .finalize();
  }

  /**
   * Checks if an AABB is empty (has infinite bounds).
   * An AABB is considered empty if its lowerBound is (Infinity, Infinity) and upperBound is (-Infinity, -Infinity).
   * @returns True if the AABB is empty, false otherwise
   */
  isEmpty(): boolean {
    return (
      this.lowerBound.x === Number.POSITIVE_INFINITY &&
      this.lowerBound.y === Number.POSITIVE_INFINITY &&
      this.upperBound.x === Number.NEGATIVE_INFINITY &&
      this.upperBound.y === Number.NEGATIVE_INFINITY
    );
  }

  /**
   * Enlarges this AABB by the specified amount in all directions
   * @param amount The amount to enlarge by
   * @returns This AABB for chaining
   */
  enlarge(amount: number): this {
    this.lowerBound.x -= amount;
    this.lowerBound.y -= amount;
    this.upperBound.x += amount;
    this.upperBound.y += amount;
    return this;
  }

  /**
   * Enlarges this AABB by the specified x and y amounts in each direction
   * @param vec The Vec2 containing x and y amounts to enlarge by
   * @returns This AABB for chaining
   */
  enlarge2D(vec: Vec2): this {
    this.lowerBound.x -= vec.x;
    this.lowerBound.y -= vec.y;
    this.upperBound.x += vec.x;
    this.upperBound.y += vec.y;
    return this;
  }

  /**
   * Divides both vectors of this AABB by a scalar
   * @param scalar The scalar to divide by
   * @returns This AABB for chaining
   */
  div(scalar: number): this {
    this.lowerBound.div(scalar);
    this.upperBound.div(scalar);
    return this;
  }

  /**
   * Floors both vectors of this AABB
   * @returns This AABB for chaining
   */
  floor(): this {
    this.lowerBound.floor();
    this.upperBound.floor();
    return this;
  }

  /**
   * Merges this AABB with another AABB, extending the bounds to encompass both
   * @param other The AABB to merge with
   * @returns This AABB for chaining
   */
  merge(other: AABB): this {
    this.lowerBound.x = Math.min(this.lowerBound.x, other.lowerBound.x);
    this.lowerBound.y = Math.min(this.lowerBound.y, other.lowerBound.y);
    this.upperBound.x = Math.max(this.upperBound.x, other.upperBound.x);
    this.upperBound.y = Math.max(this.upperBound.y, other.upperBound.y);
    return this;
  }

  /**
   * Merges this AABB with a point, extending the bounds to include the point if necessary.
   * @param point The point to include in the bounds
   * @returns This AABB for chaining
   */
  mergeWithPoint(point: Vec2): this {
    this.lowerBound.x = Math.min(this.lowerBound.x, point.x);
    this.lowerBound.y = Math.min(this.lowerBound.y, point.y);
    this.upperBound.x = Math.max(this.upperBound.x, point.x);
    this.upperBound.y = Math.max(this.upperBound.y, point.y);
    return this;
  }

  /**
   * Rounds the coordinates of the bounds to a consistent precision to avoid floating-point errors.
   * @returns This AABB for chaining
   */
  finalize(): this {
    this.lowerBound.finalize();
    this.upperBound.finalize();
    return this;
  }

  /**
   * Gets the center point of this AABB.
   * Calculated as the average of the lower and upper bounds.
   * @returns A new Vec2 representing the center point
   */
  getCenter() {
    return new Vec2(
      (this.lowerBound.x + this.upperBound.x) / 2,
      (this.lowerBound.y + this.upperBound.y) / 2,
    );
  }

  /**
   * Creates a new AABB instance with the same bounds as this one.
   * @returns A new AABB instance with cloned bounds
   */
  clone(): AABB {
    return new AABB(this.lowerBound.clone(), this.upperBound.clone());
  }
}
